import { DialogConfirm } from '../elements/DialogConfirm';
import { FormProvider, useForm } from 'react-hook-form';
import { AssetEntryForm } from './AssetEntry';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { createSubmittedAssets } from 'services/startInspection';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { ButtonCustom } from '../elements/ButtonCustom';
import { useEffect } from 'react';
import { AssetDevice } from 'app/pages/ValidationsPage/type';
import { isRuntimeTriggerType } from 'store/triggerType';
type AssetEntry = {
  assetName: string;
  assetId1: string;
  assetId2?: string;
  assetType: string;
  lastMaintenance: string;
  // criticality: string;
  criticalityKey: string;
  modelId: string;
  manufacturer: string;
  manufacturerName: string;
  note?: string;
  otherNote?: string;
  photos?: File[];
  maintenanceEnabled: boolean;
  maintenanceConfigs: AssetDevice[]; // user-editable, includes iotDeviceId/iotDeviceName
};
const CreateAssetEntryFormSchema = yup.object({
  assetName: yup.string().required('Asset Name is required'),
  assetId1: yup.string().optional(),
  assetId2: yup.string().optional(),
  assetType: yup.string().required('Asset type is required'),
  lastMaintenance: yup.string().optional(),
  // criticality: yup.string().required('Criticality is required'),
  criticalityKey: yup.string().required('Criticality is required'),
  modelId: yup.string().required('Model is required'),
  model: yup.string().when('modelId', {
    is: (v: string) => v === '0',
    then: (s) => s.required('Model is required'),
    otherwise: (s) => s.optional()
  }),
  manufacturer: yup.string().optional(),
  manufacturerName: yup.string().when('manufacturer', {
    is: (v: string) => v === '0',
    then: (s) => s.required('Manufacturer Name is required'),
    otherwise: (s) => s.optional()
  }),
  note: yup.string().optional(),
  otherNote: yup.string().optional(),
  photos: yup
    .array()
    .of(yup.mixed<File>().required())
    .required()
    .min(1, 'At least one photo is required'),
  maintenanceConfigs: yup.array().when('maintenanceEnabled', {
    is: true,
    then: (s) =>
      s
        .of(
          yup.object({
            maintenanceTriggerTypeId: yup.string().optional().nullable(),
            triggerSubTypeId: yup.string().optional().nullable(),
            calculationType: yup.string().optional().nullable(),
            unitId: yup.string().optional().nullable(),
            frequencyType: yup
              .string()
              .oneOf(['INTERVAL', 'RANGE'])
              .required('Frequency type is required'),
            maintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'INTERVAL',
                then: (s) => s.required('Maintenance frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            minMaintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'RANGE',
                then: (s) => s.required('Min frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            maxMaintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'RANGE',
                then: (s) => s.required('Max frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            notificationWindow: yup
              .number()
              .typeError('Notification Window is required')
              .required('Notification Window is required')
              .min(1, 'Must be greater than 0'),
            counterSource: yup.string(),
            iotDeviceId: yup.string().when('counterSource', {
              is: 'iot',
              then: (s) => s.required('IoT Device ID is required'),
              otherwise: (s) => s.optional()
            }),
            iotDeviceName: yup.string().when('counterSource', {
              is: 'iot',
              then: (s) => s.required('IoT Device Name is required'),
              otherwise: (s) => s.optional()
            })
          })
        )
        .min(1, 'At least one maintenance config is required'),
    otherwise: (s) => s.optional()
  })
});
type ModalCreateAssetEntryFormProps = {
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
  roomDetail?: {
    id?: number;
    name?: string;
  };
  submittedId: number;
};
export const ModalCreateAssetEntryForm = ({
  open,
  onClose,
  roomDetail,
  onRefresh,
  submittedId
}: ModalCreateAssetEntryFormProps) => {
  const { clientName } = useParams();

  const methods = useForm<AssetEntry>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(CreateAssetEntryFormSchema) as any,
    defaultValues: {
      assetName: '',
      assetId1: '',
      assetId2: '',
      assetType: '',
      lastMaintenance: undefined,
      // criticality: '',
      criticalityKey: '',
      modelId: '',
      manufacturer: '',
      manufacturerName: '',
      note: '',
      otherNote: '',
      photos: [],
      maintenanceEnabled: false,
      maintenanceConfigs: []
    }
  });
  useEffect(() => {
    if (!open) {
      methods.reset({
        assetName: '',
        assetId1: '',
        assetId2: '',
        assetType: '',
        lastMaintenance: undefined,
        // criticality: '',
        criticalityKey: '',
        modelId: '',
        manufacturer: '',
        manufacturerName: '',
        note: '',
        otherNote: '',
        photos: [],
        maintenanceEnabled: false,
        maintenanceConfigs: []
      });
    }
  }, [methods, open]);
  const handleSubmitFormAssetEntry = async (value: AssetEntry) => {
    // Build payload for API
    const payload = {
      ...value,
      name: value.assetName || '',
      typeId: Number(value.assetType),
      // criticality: Number(value.criticality),
      criticalityKey: value.criticalityKey,
      note: value.note,
      otherNote: value.otherNote,
      modelId: Number(value.modelId),
      assetCustomId: value.assetId1,
      assetCustomId2: value.assetId2,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      entries: (value as any).entries?.map((e: any) => ({
        fieldId: Number(e.fieldId),
        value: String(e.value ?? ''),
        dropdownValueId: e.dropdownValueId ? Number(e.dropdownValueId) : undefined,
        dropdownOtherValue: e.dropdownOtherValue || undefined
      })),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oemAttributes: (value as any).oemAttributes?.map((attr: any) => ({
        label: attr.label,
        value: attr.value ?? '',
        isDropdown: attr.isDropdown ?? false,
        dropdownId: attr.dropdownId ?? null,
        dropdownValue: attr.dropdownValue ?? null
      })),
      altText: `${clientName} ${roomDetail?.name} ${value.assetName || dayjs().format('YYYY-MM-DD HH:mm:ss')}`,
      photos: value.photos || [],
      manufacturer: value.manufacturer,
      manufacturerId: value.manufacturer ? Number(value.manufacturer) : undefined,
      lastMaintenance: dayjs(value.lastMaintenance).toISOString(),
      maintenanceEnabled: value.maintenanceEnabled,
      assetDevices: value.maintenanceEnabled // ← ADD
        ? value.maintenanceConfigs.map((c) => ({
            maintenanceTriggerTypeId: c.maintenanceTriggerTypeId
              ? Number(c.maintenanceTriggerTypeId)
              : undefined,
            triggerSubTypeId: c.triggerSubTypeId ? Number(c.triggerSubTypeId) : undefined,
            // Never shown/edited here — just carried through from the master
            // config. Runtime always sends unitId as null.
            calculationType: c.calculationType || undefined,
            unitId: isRuntimeTriggerType(c.maintenanceTriggerTypeId)
              ? null
              : c.unitId
                ? Number(c.unitId)
                : undefined,
            frequencyType: c.frequencyType,
            maintenanceFrequency: c.maintenanceFrequency,
            minMaintenanceFrequency: c.minMaintenanceFrequency,
            maxMaintenanceFrequency: c.maxMaintenanceFrequency,
            notificationWindow: c.notificationWindow,
            counterSource: c.counterSource,
            iotDeviceId: c.iotDeviceId,
            iotDeviceName: c.iotDeviceName
          }))
        : []
    };

    await createSubmittedAssets({ payload, submittedId });
    onRefresh();
    onClose();
  };
  const { asyncCallback: asyncHandleSubmitForm } = useAsyncCallback(handleSubmitFormAssetEntry, []);

  return (
    <DialogConfirm textTitle={'Create Asset Entry'} maxWidth={700} isOpen={open} onClose={onClose}>
      <FormProvider {...methods}>
        <AssetEntryForm />
        <ButtonCustom
          style={{ marginTop: '30px' }}
          label={'Submit'}
          onClick={methods.handleSubmit(asyncHandleSubmitForm)}
        />
      </FormProvider>
    </DialogConfirm>
  );
};
